import React, { useState, useEffect, Fragment, lazy, Suspense } from 'react';
import './app.scss';
import Header from './components/Header';
import Footer from './components/Footer';
import Router from './Router';
import axios from 'axios';
import { Workbox } from 'workbox-window';
import { AppContext } from './context/appData';
import { ThemeProvider } from 'styled-components';
import {
  _postToLocalStorage,
  _getFromLocalStorage,
  _convertObjectKeysToCamelCase,
  _getPageTitleName,
  _changeFavicon,
  _isModalVisible,
  _createCookie,
  _getCookie,
  _deleteCookie,
} from './utils/helpers';
import { ToastProvider } from 'react-toast-notifications';
import Loader from './components/Loader';
import { GET } from './utils/api';
import { useLocation, useHistory } from 'react-router-dom';
import { asset_url, store_url, host_name } from './utils/config';
import { ProductProvider } from './context/ProductContext';
//import HeaderScript from "./components/HeaderScript";
import apiRequest from './utils/api/apiRequest';
import { retailStoreApi } from './utils/api/microserviceConfig';
import FooterWrapper from './components/FooterWrapper';
import { Analytics } from './utils/Analytics';
import { useTranslation } from 'react-i18next';
import ChatBox from './components/ChatBox';
const AppUpdate = lazy(() => import('./components/AppUpdate'));
const AppNotification = lazy(() => import('./components/AppNotification'));

let wb;
const registerServiceWorker =
  process.env.NODE_ENV === 'production' || process.env.REACT_APP_RUN_SW;

if ('serviceWorker' in navigator && registerServiceWorker) {
  wb = new Workbox('sw.js');
  wb.register();
}

let installPrompt;
//create message broadcast channel
let channel;
if (window.BroadcastChannel) {
  channel = new BroadcastChannel('app-messages');
}

function App() {
  const location = useLocation();
  const history = useHistory();

  const { i18n } = useTranslation();
  const [pageTitle, setPageTitle] = useState('');
  const [prevPage, setPrevPage] = useState('');
  const [storeDetails, updateStoreDetails] = useState(
    _getFromLocalStorage('storeDetails') || {}
  );
  const [themeColor, updateThemeColor] = useState(
    _getFromLocalStorage('themeColor' || '')
  );
  const [loaderColor, updateLoaderColor] = useState('#d4d5d8');
  const [phoneNumberStatus] = useState(
    _getFromLocalStorage('phoneNumberStatus') || 0
  );

  const [customerStatus, setCustomerStatus] = useState(
    [1].includes(phoneNumberStatus)
      ? 'phoneAndAddressAdded'
      : [2].includes(phoneNumberStatus)
      ? 'phoneAdded'
      : 'loggedOut'
  );

  const [signingOut, setSigningOut] = useState(false);

  const [appDownloadStatus, setAppDownloadStatus] = useState('unknown');

  const [showAppUpdate, setShowAppUpdate] = useState(false);
  const [pushNotificationPrompt, setPushNotificationPrompt] =
    useState('hidden');

  const [redirectUrl, setRedirectUrl] = useState('');

  useEffect(() => {
    getRedirectUrl();
    window.scrollTo(0, 0);

    updateServiceWorker();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (pageTitle) {
      Analytics.logEvent({
        eventName: 'SCREEN_VIEW',
        eventData: { currentScreenName: pageTitle },
      });
    }
  }, [pageTitle]);

  useEffect(() => {
    if (wb) {
      wb.addEventListener('message', getPostMessage);
    }

    window.addEventListener('appinstalled', logAppInstallation);

    listenForBroadcastMessages();
    checkRedirectUrl();
    return () => {
      if (wb) {
        wb.removeEventListener('message', getPostMessage);
      }

      window.removeEventListener('appinstalled', logAppInstallation);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function checkRedirectUrl() {
    //check if user was redirected here
    if (location.search && location.search.includes('oldAppUser')) {
      // Analytics.logEvent({ eventName: "OpenedNewOmsUrl" });
      _createCookie('isOldAppUser', true, 80000000);
    }
  }

  function getRedirectUrl() {
    const newUrl =
      host_name === 'shop.mcpl.com.ng'
        ? 'https://shop.omnibiz.com/#/'
        : host_name === 'multiprostore.now.sh'
        ? 'https://omnibizretail.now.sh/#/'
        : '';
    if (newUrl) {
      setRedirectUrl(`${newUrl}?oldAppUser=true`);
    }
  }

  function updateServiceWorker() {
    if (channel) {
      channel.postMessage({
        action: 'updateServiceWorker',
      });
    }
  }

  useEffect(() => {
    if (storeDetails.storeLogo) {
      _changeFavicon(asset_url + storeDetails.storeLogo);
    }
  }, [storeDetails]);

  const [appVersion, setAppVersion] = useState({});

  function getPostMessage(event) {
    const { action } = event.data;
    if (action === 'updateApp') {
      updateApp(event);
    }
    if (action === 'refreshApp') {
      /*
        new app code has been pushed but version wasn't changed
        refresh the app so users can start seeing latest code
      */
      refreshApp();
    }
  }

  function refreshApp() {
    let appRefreshCookie = _getCookie('appRefreshCookie');
    if (appRefreshCookie) {
      return;
    }
    //save app update cookie that will expire in 5 minutes(300 seconds)
    _createCookie('appRefreshCookie', true, 300);
    window.location.reload();
  }

  function updateApp(event) {
    //check if any modal is open in the app
    if (!_isModalVisible()) {
      setShowAppUpdate(true);
      setAppVersion(event.data.versionDetails);
      //Analytics.logEvent({ eventName: "AppUpdate" });
    } else {
      //if a modal is open, try showing update again in 20 seconds
      setTimeout(() => {
        updateApp(event);
      }, 20000);
    }
  }

  function logAppInstallation() {
    // Analytics.logEvent({ eventName: "OmsAppInstallation" });
    if (_getCookie('isOldAppUser')) {
      //  Analytics.logEvent({ eventName: "NewOmsAppInstallation" });
    }
  }

  const reloadOtherTabs = () => {
    setTimeout(() => {
      if (channel) {
        channel.postMessage({
          action: 'reload',
        });
      }
    }, 500);
  };

  const logOutUser = async () => {
    setSigningOut(true);

    for (let key in localStorage) {
      if (!['storeDetails', 'themeColor', 'customerType'].includes(key)) {
        localStorage.removeItem(key);
      }
    }

    let response = await apiRequest({
      api: retailStoreApi,
      method: 'post',
      url: 'Signout',
    });

    if (response && !response.error) {
      _deleteCookie('accessToken');
      _deleteCookie('refreshToken');
      _deleteCookie('expiryDate');
      _deleteCookie('checkTermsAndCondition');

      sessionStorage.clear();

      i18n.changeLanguage('ng');

      for (let key in localStorage) {
        if (!['storeDetails', 'themeColor', 'i18nextLng'].includes(key)) {
          localStorage.removeItem(key);
        }
      }
      //delete the token from header to prevent getting old user data
      delete axios.defaults.headers.common['Authorization'];

      reloadOtherTabs();
    }
    setSigningOut(false);
  };

  async function updateCustomerStatus(status) {
    if (status === 'loggedOut') {
      await logOutUser();
    }
    if (status === 'phoneAdded') {
      history.push('/registration/verify-location?incompleteRegistration=true');
    }
    setCustomerStatus(status);
  }

  useEffect(() => {
    async function getStoreDetails() {
      const result = await GET(
        `store/GetStoreNameMaster?StoreUrl=${store_url}`
      );

      if (result.StoreName && result.StoreName.length > 0) {
        let storeInfo = _convertObjectKeysToCamelCase(result.StoreName[0]);
        //update app favicon

        _postToLocalStorage('storeDetails', storeInfo);

        _postToLocalStorage('themeColor', storeInfo.colorCode || '#015fd1');
        updateThemeColor(storeInfo.colorCode || '#0115fd1');
        updateLoaderColor('');
        if (
          !_getFromLocalStorage('manifestGenerated') &&
          !_getFromLocalStorage('manifestGenerationFailed')
        ) {
          window.location.reload();
        }
        setTimeout(() => {
          updateStoreDetails(storeInfo);
        }, 500);
      }
    }
    getStoreDetails();

    if (!_getFromLocalStorage('requestHeaders')) {
      _postToLocalStorage('requestHeaders', {
        headers: {
          contentType: 'application/json',
          source: 'PWA',
          BusinessId: 76,
          countrycode: 1,
        },
      });
    }
  }, []);

  useEffect(() => {
    window.addEventListener('beforeinstallprompt', e => {
      e.preventDefault();
      if (!installPrompt) {
        setAppDownloadStatus('readyToDownload');
      }

      installPrompt = e;
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let pageTitleName = _getPageTitleName(location, storeDetails);

    setPageTitle(pageTitleName ? pageTitleName.split('- ')[1] : '');
    document.title = pageTitleName || '';
  }, [location, storeDetails]);

  function updatePageTitle(title) {
    setPageTitle(title);
    // document.title = title || "";
  }

  function updatePrevPage(prevPage) {
    setPrevPage(prevPage);
  }

  function downloadApp() {
    setAppDownloadStatus('downloaded');

    if (installPrompt) {
      installPrompt.prompt();
      installPrompt.userChoice.then(choiceResult => {
        if (choiceResult.outcome === 'accepted') {
        } else {
        }
      });
    }
  }

  function cancelAppDownload() {
    setAppDownloadStatus('cancelled');
  }

  function listenForBroadcastMessages() {
    if (channel) {
      channel.addEventListener('message', event => {
        if (event.data && event.data.action) {
          const action = event.data.action;
          if (action === 'reload') {
            window.location.reload();
          }
        }
      });
    }
  }

  return (
    <div className="app">
      <AppContext.Provider
        value={{
          pageTitle: pageTitle,
          updatePageTitle: updatePageTitle,
          updatePrevPage: updatePrevPage,
          theme: { color: themeColor },
          updateCustomerStatus: updateCustomerStatus,
          storeDetails: storeDetails,
          appDownloadStatus: appDownloadStatus,
          downloadApp: downloadApp,
          cancelAppDownload: cancelAppDownload,
          installPrompt: installPrompt,
        }}
      >
        <Suspense fallback={<Loader fixed />}>
          {signingOut ? (
            <Loader fixed={true} />
          ) : storeDetails.businessId ? (
            <>
              <ToastProvider placement="bottom-left" autoDismissTimeout={2500}>
                <ThemeProvider theme={{ color: themeColor }}>
                  <ProductProvider>
                    <Header
                      title={redirectUrl ? '' : pageTitle}
                      prevPage={prevPage}
                      theme={{ color: themeColor }}
                      customerStatus={customerStatus}
                    />

                    <Fragment>
                      <div className="app__body">
                        <Router customerStatus={customerStatus} />
                      </div>
                      <ChatBox />
                      <Footer />
                      <Suspense
                        fallback={<Loader fixed={true} color={loaderColor} />}
                      >
                        {showAppUpdate && <AppUpdate version={appVersion} />}
                        {pushNotificationPrompt === 'visible' && (
                          <AppNotification
                            close={() => setPushNotificationPrompt('hidden')}
                          />
                        )}
                      </Suspense>
                    </Fragment>

                    <FooterWrapper />
                  </ProductProvider>
                </ThemeProvider>
              </ToastProvider>
            </>
          ) : (
            <Loader fixed={true} color={loaderColor} />
          )}
        </Suspense>
      </AppContext.Provider>
    </div>
  );
}

export default App;
